import { Button, FormInstance, message, Spin, Typography } from 'antd';
import InfoSvg from 'icons/emp/info.svg';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  CheckOutlined,
  DeleteOutlined,
  HolderOutlined,
  PlusOutlined,
} from '@ant-design/icons';

const { Text } = Typography;
import Icon from '@ant-design/icons';
import {
  useGetBlockEvents,
  useGetNetworkConfigures,
  useCreateNetworkConfigures,
  useUpdateNetworkConfigures,
  useDeleteNetworkConfigures,
  useReorderNetworkConfigures,
} from '@api/emp/queryHooks';
import { INETWORK_CONFIGURES } from '@src/api/emp/types';
import { useCreatePermissions } from '@src/hooks/usePermissions';
import { cleanAndValidateConfiguration } from '@components/configure-network-panel/utils/trimUtils';
import NetworkConfigView from './components/NetworkConfigView';
import NetworkConfigEdit from './components/NetworkConfigEdit';
import styled from 'styled-components';
import { rgba } from 'polished';
import { Wrapper } from './styled';

export const BulletCustom = styled.span`
  font-size: 14px;
  position: absolute;
  left: 0;
  top: 0.8em;
  transform: translateY(-50%);
  width: 5px;
  height: 5px;
  background-color: rgba(119, 119, 119, 1);
  border-radius: 50%;
`;

const Disclaimer = () => {
  return (
    <div style={{ color: rgba(0, 0, 0, 0.88), padding: '12px 24px', background: '#FFF7E6', marginBottom: 8 }}>
      <Icon component={InfoSvg} style={{ marginRight: 8, fontSize: 16, color: '#FA8C16' }} />
      <span style={{ fontWeight: 700 }}>Disclaimer: </span>
      <span>
        I hereby certify that the specified keys do NOT contain any sensitive or personal data and
        authorize Conviva for collecting these fields by the sensor SDK.
      </span>
    </div>
  );
};

interface SortableRuleItemProps {
  id: string;
  disabled: boolean;
  children: React.ReactNode;
}

const SortableRuleItem: React.FC<SortableRuleItemProps> = ({ id, disabled, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id,
    disabled,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style}>
      {!disabled && (
        <HolderOutlined
          {...attributes}
          {...listeners}
          style={{
            cursor: 'grab',
            color: '#999',
            fontSize: 14,
            position: 'absolute',
            left: 4,
            top: 14,
            zIndex: 1,
          }}
        />
      )}
      {children}
    </div>
  );
};

const urlConditionOptions = [
  { label: 'All', value: 'All' },
  { label: 'Contains...', value: 'Contains...' },
];

const ConfigureNetworkPanel = () => {
  const {
    data: configures,
    isFetching: isGetNetworkConfiguresFetching,
  } = useGetNetworkConfigures();
  const { data: blockEvents } = useGetBlockEvents();
  const { mutate: createNetworkConfigures, isLoading: isCreateLoading } =
    useCreateNetworkConfigures();
  const { mutate: updateNetworkConfigures, isLoading: isUpdateLoading } =
    useUpdateNetworkConfigures();
  const { mutate: deleteNetworkConfigures, isLoading: isDeleteLoading } =
    useDeleteNetworkConfigures();
  const { mutate: reorderConfigs, isLoading: isReordering } = useReorderNetworkConfigures();
  const hasCreate = useCreatePermissions();

  const [localConfigurations, setLocalConfigurations] = useState<INETWORK_CONFIGURES[]>([]);
  const [editingConfigId, setEditingConfigId] = useState<string | null>(null);
  const [showWarn, setShowWarn] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [urlConditionValue, setUrlConditionValue] = useState<string>(urlConditionOptions[1].value);

  const formRef = useRef<FormInstance | null>(null);

  useEffect(() => {
    setLocalConfigurations(configures || []);
  }, [configures]);

  useEffect(() => {
    interface BlockEvent { event_name: string }
    if (blockEvents?.data?.find((item: BlockEvent) => item.event_name === 'conviva_network_request')) {
      setShowWarn(true);
    }
  }, [blockEvents]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = localConfigurations.findIndex((item) => item.id === active.id);
    const newIndex = localConfigurations.findIndex((item) => item.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = [...localConfigurations];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);
    setLocalConfigurations(reordered);

    reorderConfigs(
      reordered.map((item) => item.id),
      {
        onSuccess: () => {
          message.success('Order updated successfully');
        },
        onError: () => {
          message.error('Failed to reorder configurations');
          if (configures) setLocalConfigurations(configures);
        },
      },
    );
  };

  const handleNewConfiguration = () => {
    if (editingConfigId !== null) {
      message.warning('Please finish editing the current configuration first.');
      return;
    }
    setEditingConfigId('new');
    setUrlConditionValue(urlConditionOptions[1].value);
  };

  const handleEditConfiguration = (config: INETWORK_CONFIGURES) => {
    if (editingConfigId !== null && editingConfigId !== config.id) {
      message.warning('Please finish editing the current configuration first.');
      return;
    }
    setEditingConfigId(config.id);
    if (!config.configuration?.target_url) {
      setUrlConditionValue(urlConditionOptions[0].value);
    } else {
      setUrlConditionValue(urlConditionOptions[1].value);
    }
  };

  const handleDeleteConfiguration = (configId: string) => {
    deleteNetworkConfigures(
      { id: configId },
      {
        onSuccess: () => {
          message.success({
            content: (
              <div style={{ textAlign: 'left' }}>
                Configuration deleted successfully.
                <br />
                Sensors may take up to 5 minutes to reflect changes.
              </div>
            ),
            duration: 5,
          });
        },
        onError: (error: unknown) => {
          const msg = (error as { response?: { data?: { msg?: string } } }).response?.data?.msg;
          message.error(msg || 'Failed to delete configuration');
        },
      },
    );
  };

  const handleSaveConfiguration = () => {
    const form = formRef.current;
    if (!form) return;

    interface FormValues {
      name: string;
      configuration: Record<string, string[]>;
    }
    form
      .validateFields()
      .then((res: FormValues) => {
        const { cleanConfiguration, validConfigRules } = cleanAndValidateConfiguration(
          res,
          urlConditionValue,
          urlConditionOptions,
        );

        if (validConfigRules.length === 0) {
          message.error('At least one rule is required');
          return;
        }

        const queryBody = {
          name: '',
          configuration: cleanConfiguration,
        };

        if (editingConfigId === 'new') {
          createNetworkConfigures(
            { queryBody },
            {
              onSuccess: () => {
                message.success({
                  content: (
                    <div style={{ textAlign: 'left' }}>
                      Done! The new item is now created.
                      <br />
                      Sensors may take up to 5 minutes to reflect changes.
                    </div>
                  ),
                  duration: 5,
                });
                setEditingConfigId(null);
              },
              onError: (error: unknown) => {
                const msg = (error as { response?: { data?: { msg?: string } } }).response?.data?.msg;
                message.error(msg || 'Failed to create configuration');
              },
            },
          );
        } else {
          updateNetworkConfigures(
            { id: editingConfigId, queryBody },
            {
              onSuccess: () => {
                message.success({
                  content: (
                    <div style={{ textAlign: 'left' }}>
                      Saved! Your changes are now updated.
                      <br />
                      Sensors may take up to 5 minutes to reflect changes.
                    </div>
                  ),
                  duration: 5,
                });
                setEditingConfigId(null);
              },
              onError: (error: unknown) => {
                const msg = (error as { response?: { data?: { msg?: string } } }).response?.data?.msg;
                message.error(msg || 'Failed to update configuration');
              },
            },
          );
        }
      })
      .catch((err: { values: FormValues }) => {
        const { validConfigRules } = cleanAndValidateConfiguration(
          err.values,
          urlConditionValue,
          urlConditionOptions,
        );
        if (validConfigRules.length === 0) {
          message.error('At least one rule is required');
        }
      });
  };

  const handleCancelEdit = () => {
    setEditingConfigId(null);
  };

  const handleFormRef = useCallback((form: FormInstance) => {
    formRef.current = form;
  }, []);

  const renderConfigurationItem = (config: INETWORK_CONFIGURES, index: number) => {
    const isEditing = editingConfigId === config.id;

    if (isEditing) {
      return (
        <div key={config.id}>
          <div
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.06)',
              padding: '12px 16px',
              marginBottom: 16,
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 500 }}>Edit Configuration</span>
          </div>

          <div
            style={{
              marginBottom: 16,
              borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
              paddingBottom: 16,
            }}
          >
            <NetworkConfigEdit
              data={config}
              formRef={handleFormRef}
              onUrlConditionChange={setUrlConditionValue}
            />
          </div>
        </div>
      );
    }

    return (
      <SortableRuleItem
        key={config.id}
        id={config.id}
        disabled={editingConfigId !== null}
      >
        <NetworkConfigView
          config={config}
          index={index}
          onEdit={handleEditConfiguration}
          onDelete={handleDeleteConfiguration}
          isUpdating={isUpdateLoading}
          isDeleting={isDeleteLoading}
          disabled={!hasCreate}
        />
      </SortableRuleItem>
    );
  };

  const renderNewConfiguration = () => {
    if (editingConfigId !== 'new') return null;

    return (
      <div style={{ marginBottom: 16 }}>
        <NetworkConfigEdit
          isNew
          formRef={handleFormRef}
          onUrlConditionChange={setUrlConditionValue}
        />
      </div>
    );
  };

  return (
    <Spin
      spinning={
        isGetNetworkConfiguresFetching ||
        isCreateLoading ||
        isUpdateLoading ||
        isDeleteLoading ||
        isReordering
      }
    >
      <Wrapper>
        <div
          style={{
            maxHeight: 'calc(100vh - 200px)',
            overflowY: 'auto',
            paddingRight: '8px',
            paddingBottom: '100px',
          }}
        >
          <div style={{ padding: '8px 16px 0 16px' }}>
            <span
              style={{ fontSize: '20px', fontWeight: '500' }}
              data-test-id="configureNetworkPanelHeader"
            >
              Configure Network Requests
            </span>
          </div>
          <Text
            style={{
              fontSize: '14px',
              color: '#8c8c8c',
              fontWeight: 400,
              marginLeft: '16px',
              width: '95%',
            }}
            data-test-id="configureNetworkRequestDescription"
          >
            <ul style={{ listStyle: 'none', margin: 0, paddingLeft: 0 }}>
              <li style={{ position: 'relative', paddingLeft: '14px' }}>
                <BulletCustom />
                <Text
                  style={{
                    fontSize: '14px',
                    color: '#8c8c8c',
                    fontWeight: 400,
                    paddingLeft: '8px',
                    display: 'inline-block',
                  }}
                >
                  Conviva Sensor auto-collects network request events. The associated metadata
                  collected by default includes hostname, path, response code, and method. However,
                  it&apos;s common though that the truly valuable metadata is contained within
                  request / response body and headers. Examples: payment amount and currency for
                  payment API, caching and endpoint headers for CDN requests.
                </Text>
              </li>
              <li style={{ position: 'relative', paddingLeft: '14px', marginTop: '8px' }}>
                <BulletCustom />
                <Text
                  style={{
                    fontSize: '14px',
                    color: '#8c8c8c',
                    fontWeight: 400,
                    paddingLeft: '8px',
                    display: 'inline-block',
                  }}
                >
                  To address this challenge, this feature allows you to configure which fields to
                  capture, for particular requests identifiable by URL pattern rules. These captured
                  fields become dimensions you can map and analyse just like any other data, helping
                  you surface business-critical metrics.
                </Text>
              </li>
            </ul>
          </Text>
          <div>
            {!showFullDescription ? (
              <div style={{ marginTop: 4, marginLeft: '38px' }}>
                <span
                  onClick={() => setShowFullDescription(true)}
                  style={{
                    color: 'rgba(0, 70, 255, 1)',
                    cursor: 'pointer',
                    textDecoration: 'none',
                  }}
                  data-test-id="sessionReplayViewMore"
                >
                  View More
                </span>
              </div>
            ) : (
              <>
                <Text style={{ marginLeft: '16px' }}>Supported Platforms and Versions:</Text>
                <div style={{ display: 'flex' }}>
                  <ul style={{ listStyle: 'none', margin: 0 }}>
                    <li style={{ position: 'relative', paddingLeft: '14px' }}>
                      <BulletCustom />
                      <Text
                        style={{
                          fontSize: '14px',
                          color: '#8c8c8c',
                          fontWeight: 400,
                          paddingLeft: '8px',
                          display: 'inline-block',
                        }}
                      >
                        Android 1.1.0 and above
                      </Text>
                    </li>
                    <li style={{ position: 'relative', paddingLeft: '14px' }}>
                      <BulletCustom />
                      <Text
                        style={{
                          fontSize: '14px',
                          color: '#8c8c8c',
                          fontWeight: 400,
                          paddingLeft: '8px',
                          display: 'inline-block',
                        }}
                      >
                        Web (JavaScript) 0.4.3 and above
                      </Text>
                    </li>
                    <li style={{ position: 'relative', paddingLeft: '14px' }}>
                      <BulletCustom />
                      <Text
                        style={{
                          fontSize: '14px',
                          color: '#8c8c8c',
                          fontWeight: 400,
                          paddingLeft: '8px',
                          display: 'inline-block',
                        }}
                      >
                        iOS 1.0.1 and above
                      </Text>
                    </li>
                  </ul>
                </div>
                <div style={{ marginTop: 8 }}>
                  <span
                    onClick={() => setShowFullDescription(false)}
                    style={{
                      color: 'rgba(0, 70, 255, 1)',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      marginLeft: '16px',
                    }}
                    data-test-id="sessionReplayViewLess"
                  >
                    View Less
                  </span>
                </div>
              </>
            )}
          </div>
          <Disclaimer />

          {showWarn && (
            <div style={{ padding: '8px 16px', color: '#FF4D4F', fontSize: 14 }}>
              Some/All network requests have been blocked, which may affect the execution of the
              configurations below.
            </div>
          )}

          {/* Configurations bar with inline + New button */}
          <div
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.06)',
              padding: '8px 16px',
              marginBottom: 16,
              marginTop: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 500 }}>
              Configurations ({localConfigurations.length})
            </span>
            {hasCreate && editingConfigId !== 'new' && (
              <Button
                type="default"
                size="small"
                disabled={editingConfigId !== null}
                onClick={handleNewConfiguration}
                style={{
                  color:
                    editingConfigId !== null ? 'rgba(0, 0, 0, 0.25)' : 'rgba(0, 70, 255, 1)',
                  fontSize: '13px',
                  fontWeight: 400,
                  borderColor:
                    editingConfigId !== null
                      ? 'rgba(0, 0, 0, 0.15)'
                      : 'rgba(179, 200, 255, 1)',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
                icon={<PlusOutlined />}
                data-test-id="add-new-configuration-button"
              >
                New
              </Button>
            )}
          </div>

          {renderNewConfiguration()}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={localConfigurations.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              <div data-test-id="configurations-list">
                {localConfigurations.map((config, index) =>
                  renderConfigurationItem(config, index),
                )}
              </div>
            </SortableContext>
          </DndContext>

          {localConfigurations.length === 0 && editingConfigId !== 'new' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 50,
                color: 'rgba(0, 0, 0, 0.45)',
                fontSize: 14,
              }}
            >
              List is empty, please create a new configuration...
            </div>
          )}

          {/* Floating save/discard bar */}
          {editingConfigId !== null && (
            <div
              style={{
                position: 'fixed',
                left: '50%',
                bottom: 'calc(24px + env(safe-area-inset-bottom))',
                transform: 'translateX(-50%)',
                zIndex: 1000,
                background: '#fff',
                padding: '14px 50px',
                borderRadius: 16,
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.25)',
                minWidth: 420,
                maxWidth: '90vw',
              }}
              aria-live="polite"
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                }}
              >
                <span
                  style={{
                    color: 'rgba(31, 31, 31, 1)',
                    fontWeight: 400,
                    fontSize: 14,
                  }}
                  data-test-id="unsavedChangesWarning"
                >
                  Click save to apply changes
                </span>

                <div style={{ marginLeft: 'auto', display: 'flex', gap: 12 }}>
                  <Button
                    type="default"
                    icon={<DeleteOutlined />}
                    onClick={handleCancelEdit}
                    style={{
                      color: 'rgba(255, 77, 79, 1)',
                      borderColor: 'rgba(255, 77, 79, 1)',
                    }}
                    data-test-id="discard-all-button"
                  >
                    Discard All
                  </Button>

                  <Button
                    type="primary"
                    loading={isCreateLoading || isUpdateLoading}
                    disabled={isCreateLoading || isUpdateLoading}
                    icon={<CheckOutlined />}
                    onClick={handleSaveConfiguration}
                    data-test-id="save-button"
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Wrapper>
    </Spin>
  );
};

export default ConfigureNetworkPanel;
